import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { usePendingInvitations } from '../../hooks/usePendingInvitations';
import { InvitationItem } from './InvitationItem';
import { EmptyView, EMPTY_VIEW_ICONS } from '@shared/components/ui/EmptyView';
import { PendingInvitation } from '../../models/User';

interface InvitationsListProps {
  className?: string;
  // 🎯 Props opcionales para usar invitaciones externas (cuando el modal controla el polling)
  externalInvitations?: PendingInvitation[];
  externalIsLoading?: boolean;
  externalError?: Error | null;
  externalIsFetching?: boolean;
  onRefetch?: () => void;
}

/**
 * Componente que muestra la lista de invitaciones pendientes
 * Puede usar invitaciones externas (del modal) o hacer su propia llamada
 */
export const InvitationsList: React.FC<InvitationsListProps> = ({
  className = '',
  externalInvitations,
  externalIsLoading,
  externalError,
  externalIsFetching,
  onRefetch,
}) => {
  const { t } = useTranslation('usersModule');
  
  // 🎯 Si se pasan invitaciones externas, usarlas; si no, hacer la llamada propia
  const {
    invitations: hookInvitations,
    isLoading: hookIsLoading,
    error: hookError,
    refetch: hookRefetch,
    isFetching: hookIsFetching,
  } = usePendingInvitations();

  // 🎯 Usar datos externos si están disponibles, sino usar los del hook
  const invitations = externalInvitations ?? hookInvitations;
  const isLoading = externalIsLoading ?? hookIsLoading;
  const error = externalError ?? hookError;
  const isFetching = externalIsFetching ?? hookIsFetching;
  const refetch = onRefetch ?? hookRefetch;

  // Estado de carga
  if (isLoading) {
    return (
      <div className={`flex items-center justify-center py-8 ${className}`}>
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <span className="ml-2 text-sm text-complementary-text">
          {t('invitations.loading', 'Cargando invitaciones...')}
        </span>
      </div>
    );
  }

  // Estado de error
  if (error) {
    return (
      <div className={`p-4 ${className}`}>
        <div className="p-4 bg-destructive/10 border border-destructive rounded-md">
          <p className="text-sm text-destructive mb-2">
            {t('invitations.errors.loadFailed', 'Error al cargar invitaciones')}
          </p>
          <p className="text-xs text-complementary-text mb-4">
            {(error as Error).message || t('invitations.errors.unexpected', 'Error inesperado')}
          </p>
          <button
            onClick={() => refetch()}
            className="text-sm text-primary hover:underline"
            data-testid="retry-invitations"
          >
            {t('invitations.retry', 'Reintentar')}
          </button>
        </div>
      </div>
    );
  }

  // Estado vacío
  if (invitations.length === 0) {
    return (
      <div className={`flex flex-col items-center justify-center py-8 px-4 text-center ${className}`}>
        <div className="text-complementary-text text-sm">
          {t('invitations.empty', 'No hay invitaciones pendientes')}
        </div>
        <div className="text-xs text-complementary-text mt-2">
          {t(
            'invitations.emptyDesc',
            'Todas las invitaciones han sido aceptadas o canceladas'
          )}
        </div>
      </div>
    );
  }

  // Lista de invitaciones
  return (
    <div className={`space-y-3 ${isFetching ? 'opacity-50 pointer-events-none' : ''} ${className}`}>
      {invitations.map((invitation) => (
        <InvitationItem key={invitation.id} invitation={invitation} />
      ))}
    </div>
  );
};

